import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { Scheduler, CAL_MONTHS } from "./shared/calendar";

// Config injected by the Razor view (field definitions). The scheduling calendar is fetched at
// runtime from /Intake/Availability for this hostname.
const CONFIG = (typeof window !== "undefined" && window.__INTAKE__) || { fields: [], groups: [] };

// The design splits Step 1 into two labeled sections. Fields are rendered in these explicit orders
// (anything defined by the field set but not listed here is appended to Personal Information, so a
// newly-added field still shows up). Only Email spans the full two columns.
const PERSONAL_ORDER = ["Email", "NAMfirst", "NAMlast", "NAMmid", "NAMsuf", "DOBChild", "POBCode", "SOCNumberChild"];
const DEMOGRAPHIC_ORDER = ["RACcode", "SEXcode", "HGT", "WGT", "EYEcode", "HAIcode"];
const FULL_WIDTH = new Set(["Email"]);
const PLACEHOLDERS = { Email: "you@example.com" };
// Hard cap on characters typed into a field (UI convenience; the server still validates authoritatively).
const INPUT_MAXLENGTH = { WGT: 3 };

// Service-center contact for the confirmation page's support line (injected by the Razor view).
// Walk-in client (the standby site): applicants are already on site, so there is nothing to book.
// Scheduling is dropped entirely — no availability fetch, no Step 2 calendar, no slot on submit.
const WALK_IN = CONFIG.walkIn === true;

const SERVICE_EMAIL = CONFIG.serviceEmail || "dcsa@applicantservices.com";
const SERVICE_PHONE = CONFIG.servicePhone || "";

// Post-booking "onsite intake follow-up" copy (kept in sync with the confirmation email). Edit here.
const ONSITE_STEPS = [
    ["Arrival and check-in", "Arrive 15 minutes early. Give your name and confirmation code at the front desk, or scan it at the self check-in kiosk."],
    ["Identity verification", "An operator verifies your photo ID against the details you submitted and confirms your enrollment record."],
    ["Biometric capture", "The operator guides you through the Live Scan fingerprint capture. Rolled and flat prints are taken and quality-checked on the spot."],
];
const BRING_ITEMS = [
    ["Confirmation code", "printed or on your phone."],
    ["Valid photo ID", "driver's license, passport, or state ID. Must be unexpired."],
    ["Sponsor or agency information", "if your submission requires an ORI or agency reference."],
];
const BEFORE_ITEMS = [
    "Avoid hand lotion the day of your appointment — it degrades print quality.",
    "Cover any cuts or abrasions on your fingertips, and let the operator know about them.",
    "Come alone if possible. Only the applicant is permitted in the capture area.",
    "Late arrivals over 10 minutes may need to be rescheduled.",
];

// Walk-in variants, kept in sync with ConfirmationEmail.WalkIn* in Core. Nothing about arriving
// early or arriving late applies to someone already standing at the enrollment desk.
const WALK_IN_ONSITE_STEPS = [
    ["Check in with staff", "Show the confirmation code on your screen to the staff member at the enrollment desk. You will be called when a technician is free."],
    ["Identity verification", "An operator verifies your photo ID against the details you submitted and confirms your enrollment record."],
    ["Biometric capture", "The operator guides you through the Live Scan fingerprint capture. Rolled and flat prints are taken and quality-checked on the spot."],
];
const WALK_IN_BRING_ITEMS = [
    ["Confirmation code", "on this screen, or from your confirmation email."],
    ["Valid photo ID", "driver's license, passport, or state ID. Must be unexpired."],
    ["Sponsor or agency information", "if your submission requires an ORI or agency reference."],
];
const WALK_IN_BEFORE_ITEMS = [
    "Avoid hand lotion today — it degrades print quality.",
    "Cover any cuts or abrasions on your fingertips, and let the operator know about them.",
    "Come alone if possible. Only the applicant is permitted in the capture area.",
];

// "11:15 AM" -> "11:00 AM" (appointment time minus 15 min); null when unparseable.
function arriveBy(timeStr, minutes) {
    const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((timeStr || "").trim());
    if (!m) return null;
    let h = parseInt(m[1], 10) % 12;
    if (/PM/i.test(m[3])) h += 12;
    const total = h * 60 + parseInt(m[2], 10) - (minutes || 15);
    if (total < 0) return null;
    const hh = Math.floor(total / 60);
    const mm = total % 60;
    const ap = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return h12 + ":" + String(mm).padStart(2, "0") + " " + ap;
}

function antiForgeryToken() {
    const el = document.querySelector('input[name="__RequestVerificationToken"]');
    return el ? el.value : "";
}

function looksLikeEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");
}

// Client-side field validation, mirroring the server rules (email format, regex pattern, numeric
// range, date year/age floors). Empty values return null here — "required" is handled by the gate,
// not per-field nagging. The server re-validates authoritatively on submit regardless.
function fieldError(def, fields) {
    const v = (fields[def.objectId] || "").trim();
    if (!v) return null;
    if (def.type === "Email" && !looksLikeEmail(v)) return "Enter a valid email address.";
    if (def.type === "Date" && (def.minYear != null || def.minAge != null)) {
        // The date input emits ISO yyyy-MM-dd; a partial/invalid value just isn't checked here.
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
        if (m) {
            const year = Number(m[1]);
            if (def.minYear != null && year < def.minYear) {
                return "Year must be " + def.minYear + " or later.";
            }
            if (def.minAge != null) {
                const today = new Date();
                let age = today.getFullYear() - year;
                const beforeBirthday =
                    today.getMonth() + 1 < Number(m[2]) ||
                    (today.getMonth() + 1 === Number(m[2]) && today.getDate() < Number(m[3]));
                if (beforeBirthday) age--;
                if (age < def.minAge) return "Applicants must be at least " + def.minAge + " years old.";
            }
        }
    }
    if (def.pattern && !new RegExp(def.pattern).test(v)) return def.message || "Invalid format.";
    if (def.min != null || def.max != null) {
        const n = Number(v);
        if (!Number.isInteger(n) || (def.min != null && n < def.min) || (def.max != null && n > def.max)) {
            return def.message || "Value is out of the allowed range.";
        }
    }
    return null;
}

// Strip a trailing unit/hint parenthetical for the rail checklist (e.g. "Weight (lbs)" -> "Weight").
const shortLabel = (display) => display.replace(/\s*\([^)]*\)\s*$/, "");

const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// "2026-08-10" -> "Monday, August 10, 2026" (for the scheduling-window rail card).
function formatLongDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return WEEKDAYS_LONG[dt.getDay()] + ", " + CAL_MONTHS[m - 1] + " " + d + ", " + y;
}

// Earliest/latest open day across every location — drives the "Scheduling window" rail card.
function computeWindow(locations) {
    const isos = [];
    (locations || []).forEach((l) => (l.days || []).forEach((d) => isos.push(d.isoDate)));
    if (!isos.length) return null;
    isos.sort();
    return { start: isos[0], end: isos[isos.length - 1] };
}

// Height as two boxes (feet 4-7 + inches 0-11) that combine into the 3-digit backend value
// (e.g. 5 ft 10 in -> "510"). Backend/validation are unchanged; this is purely presentational.
function HeightInput({ objectId, value, onChange, disabled }) {
    const parse = (v) => (/^[4-7]\d\d$/.test(v || "")
        ? { ft: v[0], inch: String(Number(v.slice(1))) }
        : { ft: "", inch: "" });
    const seed = parse(value);
    const [ft, setFt] = useState(seed.ft);
    const [inch, setInch] = useState(seed.inch);

    const push = (nextFt, nextInch) => {
        const combined = nextFt !== "" && nextInch !== ""
            ? nextFt + String(nextInch).padStart(2, "0")
            : "";
        onChange(objectId, combined);
    };

    return (
        <div className="intake-height">
            <select aria-label="Feet" value={ft} disabled={disabled}
                onChange={(e) => { setFt(e.target.value); push(e.target.value, inch); }}>
                <option value="">Feet</option>
                {[4, 5, 6, 7].map((f) => <option key={f} value={f}>{f} ft</option>)}
            </select>
            <select aria-label="Inches" value={inch} disabled={disabled}
                onChange={(e) => { setInch(e.target.value); push(ft, e.target.value); }}>
                <option value="">Inches</option>
                {Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{i} in</option>)}
            </select>
        </div>
    );
}

// SSN: masked (type=password) with an eye toggle to reveal, since it's the most sensitive field.
function SsnInput({ objectId, value, onChange }) {
    const [reveal, setReveal] = useState(false);
    return (
        <div className="intake-masked">
            <input
                id={objectId}
                type={reveal ? "text" : "password"}
                value={value || ""}
                inputMode="numeric"
                autoComplete="off"
                maxLength={9}
                onChange={(e) => onChange(objectId, e.target.value)}
            />
            <button
                type="button"
                className="intake-reveal"
                aria-label={reveal ? "Hide Social Security Number" : "Show Social Security Number"}
                aria-pressed={reveal}
                onClick={() => setReveal((r) => !r)}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                    <circle cx="12" cy="12" r="3" />
                    {reveal ? <line x1="3" y1="3" x2="21" y2="21" /> : null}
                </svg>
            </button>
        </div>
    );
}

// Touch-primary devices keep the native <select> (the OS picker is the better mobile UX); pointer
// devices get the searchable combobox below. Use (pointer: coarse) only — it reflects the PRIMARY
// pointer, so a touchscreen laptop (mouse/trackpad primary) correctly gets the combobox. Do NOT test
// "ontouchstart" in window: it is present on many desktop/laptop browsers and would wrongly force the
// native <select> everywhere (the "can't type in the box" bug).
const IS_TOUCH = typeof window !== "undefined" &&
    "matchMedia" in window && window.matchMedia("(pointer: coarse)").matches;

// The list renders in full — every match, always. Do not reintroduce a cap: a cap can hide the one
// row someone needs, and did (see matchRank). Ranking decides the order; nothing decides what exists.
// The largest lookup is POB at 955 options, which the browser renders comfortably.

// Rank one option against the query so the likeliest intent floats to the top:
//   0 exact  ·  1 display starts with the query  ·  2 query starts a word inside it  ·  3 mid-word
// Returns -1 when it doesn't match at all.
//
// Why this exists: plain substring filtering buried the exact hit. "india" matches 179 of the 955
// POB options — nearly all of them "…Band of … Indians, CA" tribal entries — and the country itself
// landed 69th in table order, past the cap and therefore unreachable. Typing more could not help,
// because "india" is already the whole word. Ranking puts the exact match first for any such query.
function matchRank(display, query) {
    const d = display.toLowerCase();
    const at = d.indexOf(query);
    if (at < 0) return -1;
    if (d === query) return 0;
    if (at === 0) return 1;
    // A preceding letter/digit means the query landed mid-word ("Indians"); anything else — a space,
    // hyphen, comma, parenthesis — means it starts a word ("Bassas Da India").
    return /[a-z0-9]/.test(d.charAt(at - 1)) ? 3 : 2;
}

// Searchable dropdown for Lookup fields: type to filter by display text, full keyboard support, but
// still emits the option CODE as the value — so validation/submission are unchanged from the <select>.
function Combobox({ id, options, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const rootRef = useRef(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);
    const [placement, setPlacement] = useState({ left: 0, width: 0, top: 0, bottom: null, maxHeight: 260 });

    const selected = options.find((o) => o.code === value) || null;

    // The dropdown renders position:fixed (so the card's overflow:hidden can't clip it, and a later
    // section can't paint over it). Measure the input and flip upward when there is more room above.
    const updatePlacement = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const gap = 4;
        const below = window.innerHeight - r.bottom - gap;
        const above = r.top - gap;
        const up = below < 200 && above > below;
        setPlacement({
            left: r.left, width: r.width, up,
            top: up ? null : r.bottom + gap,
            bottom: up ? window.innerHeight - r.top + gap : null,
            maxHeight: Math.max(120, Math.min(260, (up ? above : below) - 8)),
        });
    }, []);

    const q = query.trim().toLowerCase();

    // Matches, best-first. Ties keep the table's own order (U.S. states first, then alphabetical) —
    // Array.prototype.sort is stable, and the index tie-break makes that explicit rather than assumed.
    const matches = useMemo(() => {
        if (!q) return options;
        return options
            .map((option, index) => ({ option, index, rank: matchRank(option.display, q) }))
            .filter((m) => m.rank >= 0)
            .sort((a, b) => a.rank - b.rank || a.index - b.index)
            .map((m) => m.option);
    }, [options, q]);

    // Close when focus/click leaves the widget.
    useEffect(() => {
        function onDocDown(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setQuery(""); }
        }
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
    }, []);

    // Keep the fixed dropdown pinned to the input while open (scroll/resize); capture-phase catches
    // scrolls in any scroll container, not just the window.
    useEffect(() => {
        if (!open) return;
        updatePlacement();
        window.addEventListener("scroll", updatePlacement, true);
        window.addEventListener("resize", updatePlacement);
        return () => {
            window.removeEventListener("scroll", updatePlacement, true);
            window.removeEventListener("resize", updatePlacement);
        };
    }, [open, updatePlacement]);

    // Keep the keyboard-highlighted option in view.
    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.querySelector(".is-active");
        if (el) el.scrollIntoView({ block: "nearest" });
    }, [active, open]);

    function commit(opt) { onChange(opt ? opt.code : ""); setOpen(false); setQuery(""); }

    function onKeyDown(e) {
        if (e.key === "ArrowDown") { setOpen(true); setActive((a) => Math.min(a + 1, matches.length - 1)); e.preventDefault(); }
        else if (e.key === "ArrowUp") { setActive((a) => Math.max(a - 1, 0)); e.preventDefault(); }
        else if (e.key === "Enter") { if (open && matches[active]) { commit(matches[active]); e.preventDefault(); } }
        else if (e.key === "Tab") {
            // Commit the highlighted match when the user has typed a search; otherwise just close, so a
            // plain tab-through (the dropdown auto-opens on focus) selects nothing. Never preventDefault —
            // Tab must still move focus to the next field.
            if (open && query.trim() !== "" && matches[active]) { commit(matches[active]); }
            else { setOpen(false); setQuery(""); }
        }
        else if (e.key === "Escape") { setOpen(false); setQuery(""); }
    }

    return (
        <div className="ai-combo" ref={rootRef}>
            <input
                id={id}
                ref={inputRef}
                className="ai-combo__input"
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={id + "-list"}
                aria-autocomplete="list"
                aria-activedescendant={open && matches[active] ? id + "-opt-" + matches[active].code : undefined}
                autoComplete="off"
                placeholder="Type to search…"
                value={open ? query : (selected ? selected.display : "")}
                onFocus={() => { setOpen(true); setQuery(""); setActive(0); updatePlacement(); }}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
                onKeyDown={onKeyDown}
            />
            <button
                type="button" className="ai-combo__btn" tabIndex={-1}
                aria-label={value ? "Clear" : "Open"}
                onClick={() => { if (value) { commit(null); } else { setOpen((o) => !o); updatePlacement(); } }}>
                {value ? "×" : (
                    <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
                        <path d="M1 1l4 4 4-4" fill="none" stroke="#6B7688" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </button>
            {open ? (
                <ul
                    className={"ai-combo__list" + (placement.up ? " ai-combo__list--up" : "")}
                    id={id + "-list"} role="listbox" ref={listRef}
                    style={{
                        left: placement.left, width: placement.width, maxHeight: placement.maxHeight,
                        ...(placement.top != null ? { top: placement.top } : { bottom: placement.bottom }),
                    }}>
                    {matches.length === 0 ? (
                        <li className="ai-combo__empty">No matches</li>
                    ) : matches.map((o, i) => (
                        <li
                            key={o.code} id={id + "-opt-" + o.code} role="option"
                            aria-selected={o.code === value}
                            className={"ai-combo__opt" + (i === active ? " is-active" : "") + (o.code === value ? " is-selected" : "")}
                            onMouseDown={(e) => { e.preventDefault(); commit(o); }}
                            onMouseEnter={() => setActive(i)}>
                            {o.display}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function Field({ def, value, error, onChange, full }) {
    const isHeight = def.objectId === "HGT";
    const isSsn = def.objectId === "SOCNumberChild";
    const isLookup = def.type === "Lookup";
    const inputType = def.type === "Date" ? "date" : def.type === "Email" ? "email" : "text";
    return (
        <div className={"ai-field" + (full ? " ai-field--full" : "")}>
            <label className="ai-field__label" htmlFor={def.objectId}>
                {def.display}{def.required ? <span className="ai-req"> *</span> : null}
            </label>
            {isHeight ? (
                <HeightInput objectId={def.objectId} value={value} onChange={onChange} />
            ) : isSsn ? (
                <SsnInput objectId={def.objectId} value={value} onChange={onChange} />
            ) : isLookup ? (
                IS_TOUCH ? (
                    <select id={def.objectId} value={value || ""} onChange={(e) => onChange(def.objectId, e.target.value)}>
                        <option value="">Select an option…</option>
                        {(def.options || []).map((opt) => (
                            <option key={opt.code} value={opt.code}>{opt.display}</option>
                        ))}
                    </select>
                ) : (
                    <Combobox
                        id={def.objectId}
                        options={def.options || []}
                        value={value}
                        onChange={(code) => onChange(def.objectId, code)}
                    />
                )
            ) : (
                <input
                    id={def.objectId}
                    type={inputType}
                    value={value || ""}
                    placeholder={PLACEHOLDERS[def.objectId] || ""}
                    maxLength={INPUT_MAXLENGTH[def.objectId]}
                    onChange={(e) => onChange(def.objectId, e.target.value)}
                />
            )}
            {def.hint ? <span className="intake-hint">{def.hint}</span> : null}
            {error ? <span className="intake-error" role="alert">{error}</span> : null}
        </div>
    );
}

// The applicant-entered TCR on a resubmission: the quick local part of Core's TcrValidator (trimmed,
// required, printable ASCII with no spaces, 10 to 40 characters), with byte-identical messages. Deliberately NOT the gate prefix — that
// rule is server-only (POST /Appointment/ResubmitTcr) so it never ships in this bundle.
const TCR_MIN_LENGTH = 10;
const TCR_MAX_LENGTH = 40;
function checkTcr(raw) {
    const tcr = (raw || "").trim();
    if (!tcr) return { tcr: null, error: "Enter the TCR your FSO gave you." };
    // Printable ASCII, codes 33-126 (the legacy server downstream). Before length: an emoji is 2 units.
    if (!/^[\x21-\x7E]+$/.test(tcr)) {
        return { tcr: null, error: "The TCR can only contain letters, numbers and standard punctuation, with no spaces." };
    }
    if (tcr.length < TCR_MIN_LENGTH) return { tcr: null, error: "The TCR must be at least 10 characters." };
    if (tcr.length > TCR_MAX_LENGTH) return { tcr: null, error: "The TCR can be at most 40 characters." };
    return { tcr, error: null };
}

// Sticky right-rail note: progress toward unlocking Step 2 + the list of fields still needed.
function GateNote({ done, total, percent, missing }) {
    return (
        <div className="ai-rail-note" aria-live="polite">
            <div className="ai-rail-note__head">
                <span className="ai-rail-note__badge">!</span>
                <span className="ai-rail-note__title">
                    {WALK_IN ? "Before you can submit" : "Before you can schedule"}
                </span>
            </div>
            <p className="ai-rail-note__body">
                Step 2 unlocks once every required field (<span className="ai-req">*</span>) in Step 1 is complete.
            </p>
            <div className="ai-progress"><div className="ai-progress__fill" style={{ width: percent + "%" }} /></div>
            <p className="ai-progress__caption">{done} of {total} required fields complete</p>
            {missing.length ? (
                <div className="ai-still-needed">
                    <div className="ai-still-needed__eyebrow">Still needed</div>
                    <ul className="ai-still-needed__list">
                        {missing.map((label) => <li key={label}>{label}</li>)}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

// Mobile-only compact version of the gate: a sticky bar (progress + "X/Y") pinned to the top of the
// form, tap to expand the "still needed" checklist. Replaces the right-rail note on small screens
// (where the rail drops to the bottom and is easy to miss). Hidden on desktop via CSS.
function MobileGate({ done, total, percent, missing, ready }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={"ai-mobilegate" + (ready ? " is-ready" : "")}>
            <button type="button" className="ai-mobilegate__bar" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                <span className="ai-mobilegate__label">
                    {WALK_IN
                        ? (ready ? "Ready to submit" : "Before you can submit")
                        : (ready ? "Ready to schedule" : "Before you can schedule")}
                </span>
                <span className="ai-mobilegate__count">{done}/{total}</span>
                <span className={"ai-mobilegate__chev" + (open ? " is-open" : "")} aria-hidden="true">▾</span>
            </button>
            <div className="ai-mobilegate__track"><div className="ai-mobilegate__fill" style={{ width: percent + "%" }} /></div>
            {open ? (
                <div className="ai-mobilegate__panel">
                    {missing.length ? (
                        <>
                            <div className="ai-mobilegate__eyebrow">Still needed</div>
                            <ul className="ai-mobilegate__list">
                                {missing.map((label) => <li key={label}>{label}</li>)}
                            </ul>
                        </>
                    ) : (
                        <p className="ai-mobilegate__done">
                            All required fields complete — scroll down to {WALK_IN ? "submit" : "schedule"}.
                        </p>
                    )}
                </div>
            ) : null}
        </div>
    );
}

// Small green check glyph used in the booking summary + "what to bring" markers.
function CheckIcon({ size }) {
    return (
        <svg width={size || 16} height={size || 16} viewBox="0 0 24 24" fill="none" stroke="#2E7D4F"
             strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="4 12 10 18 20 6" />
        </svg>
    );
}

// The post-booking "onsite intake follow-up" page (design: design_handoff_onsite_intake_followup).
// Its own hero + booking summary, what-happens-onsite (3 steps), what-to-bring / before-you-come, and
// a support line. No reschedule/cancel bar. Read-only. A walk-in variant swaps the appointment
// wording for enrollment wording and points the code at the technician rather than the front desk.
function FollowUp({ matchCode, appt, registeredEmail, walkIn }) {
    const arrive = appt ? arriveBy(appt.time, 15) : null;
    const onsiteSteps = walkIn ? WALK_IN_ONSITE_STEPS : ONSITE_STEPS;
    const bringItems = walkIn ? WALK_IN_BRING_ITEMS : BRING_ITEMS;
    const beforeItems = walkIn ? WALK_IN_BEFORE_ITEMS : BEFORE_ITEMS;
    const contact = (
        <>
            Questions about your enrollment? Contact the service center at{" "}
            {SERVICE_PHONE ? (
                <>
                    <a href={"tel:" + SERVICE_PHONE.replace(/\s/g, "")}>{SERVICE_PHONE}</a>{" or "}
                </>
            ) : null}
            <a href={"mailto:" + SERVICE_EMAIL}>{SERVICE_EMAIL}</a>.
        </>
    );

    return (
        <div className="fu">
            <section className="fu-hero">
                <div className="fu-hero__inner">
                    <p className="fu-hero__eyebrow">Fingerprint Enrollment &middot; Trusted Workforce 2.0</p>
                    <h1 className="fu-hero__title">{walkIn ? "You're enrolled" : "You're on the schedule"}</h1>
                    <div className="fu-hero__rule" />
                    <p className="fu-hero__subhead">
                        {walkIn
                            ? "Your enrollment is submitted. Here is what to expect at the fingerprinting station, and what to have ready."
                            : "Your appointment is booked. Here is what to expect when you arrive for your onsite applicant intake, and what to bring with you."}
                    </p>
                </div>
            </section>

            <div className="fu-main">
                {/* Booking summary */}
                <div className="fu-summary">
                    <div className="fu-summary__left">
                        <div className="fu-summary__titlerow">
                            <span className="fu-check-badge"><CheckIcon size={17} /></span>
                            <h2>{walkIn ? "Your enrollment is submitted" : "Your appointment is booked"}</h2>
                        </div>
                        <p className="fu-summary__body">
                            A confirmation has been sent to{" "}
                            <strong>{registeredEmail || "the email you registered with"}</strong>.{" "}
                            {walkIn
                                ? "Keep this page open — show the code below to your fingerprint technician."
                                : "Bring your confirmation code and a valid photo ID."}
                        </p>
                        {appt ? (
                            <div className="fu-detailrow">
                                <div>
                                    <div className="fu-detail__label">Location</div>
                                    <div className="fu-detail__strong">{appt.locationName}</div>
                                    {appt.address ? <div className="fu-detail__sub">{appt.address}</div> : null}
                                </div>
                                <div>
                                    <div className="fu-detail__label">Date &amp; Time</div>
                                    <div className="fu-detail__strong">{appt.day}</div>
                                    <div className="fu-detail__sub">
                                        {appt.time}{arrive ? " — arrive by " + arrive : ""}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="fu-codepanel">
                        <div className="fu-codepanel__label">Your confirmation code</div>
                        <div className="fu-codepanel__code">{matchCode}</div>
                        <div className="fu-codepanel__caption">
                            {walkIn ? "Show this code to your fingerprint technician." : "Present this code at the front desk."}
                        </div>
                    </div>
                </div>

                {/* What happens onsite */}
                <div className="fu-card">
                    <h3 className="fu-card__title">What happens onsite</h3>
                    <p className="fu-card__sub">Plan for about 15–20 minutes from check-in to completion.</p>
                    <div className="fu-steps">
                        {onsiteSteps.map((s, i) => (
                            <div className="fu-step" key={i}>
                                <span className="fu-step__num">{i + 1}</span>
                                <div>
                                    <div className="fu-step__title">{s[0]}</div>
                                    <div className="fu-step__body">{s[1]}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="fu-status-note">
                        To check the status of your enrollment after your visit, contact the service center below.
                    </p>
                </div>

                {/* Two info cards */}
                <div className="fu-info-grid">
                    <div className="fu-card fu-card--bring">
                        <h3 className="fu-card__title fu-card__title--sm">What to bring</h3>
                        <div className="fu-list">
                            {bringItems.map((it, i) => (
                                <div className="fu-list__item" key={i}>
                                    <span className="fu-list__check"><CheckIcon size={16} /></span>
                                    <span><strong>{it[0]}</strong> — {it[1]}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="fu-card fu-card--before">
                        <h3 className="fu-card__title fu-card__title--sm">Before you come</h3>
                        <div className="fu-list">
                            {beforeItems.map((it, i) => (
                                <div className="fu-list__item" key={i}>
                                    <span className="fu-list__dot" aria-hidden="true" />
                                    <span>{it}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <p className="fu-support">{contact}</p>
            </div>
        </div>
    );
}

function IntakeForm() {
    const [fields, setFields] = useState({});
    const [errors, setErrors] = useState({});
    const [matchCode, setMatchCode] = useState(null);
    const [confirmedAppt, setConfirmedAppt] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null); // { id, locationName, day, time }
    const [slotNotice, setSlotNotice] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [avail, setAvail] = useState({ loading: true, error: false, locations: [] });

    // Resubmission mode: when the page injected a resubmitCode, the form starts behind a DOB gate, then
    // asks for the TCR (from the applicant's FSO), then pre-fills from the original (rejected)
    // transaction and submits as a brand-new one.
    const resubmitCode = CONFIG.resubmitCode || null;
    const [resubmitDob, setResubmitDob] = useState("");        // the DOB that authenticates against the original
    const [resubmitStep, setResubmitStep] = useState("dob");   // "dob" -> "tcr" -> "form"
    const [tcrInput, setTcrInput] = useState("");              // what's in the TCR box
    const [resubmitTcr, setResubmitTcr] = useState("");        // the accepted (trimmed) TCR sent on submit
    const [tcrError, setTcrError] = useState(null);
    const [resubmitBusy, setResubmitBusy] = useState(false);
    const [resubmitError, setResubmitError] = useState(null);

    // Availability is fetched once (and on reload) regardless of the gate — the rail's
    // "Scheduling window" card shows even while Step 2 is still locked.
    useEffect(() => {
        // A walk-in host has no locations, so there is nothing to fetch and the endpoint would only
        // return an empty list.
        if (WALK_IN) {
            setAvail({ loading: false, error: false, locations: [] });
            return undefined;
        }

        let cancelled = false;
        setAvail({ loading: true, error: false, locations: [] });
        fetch("/Intake/Availability", { headers: { Accept: "application/json" } })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad status"))))
            .then((data) => {
                if (!cancelled) setAvail({ loading: false, error: false, locations: (data && data.locations) || [] });
            })
            .catch(() => { if (!cancelled) setAvail({ loading: false, error: true, locations: [] }); });
        return () => { cancelled = true; };
    }, [reloadKey]);

    // Once booked, the confirmation page owns the full canvas with its own hero — hide the intake hero.
    // (The resubmit flow keeps the normal hero so its cards don't ride up over the header.)
    useEffect(() => {
        document.body.classList.toggle("has-followup", Boolean(matchCode));
    }, [matchCode]);

    // Resubmit gate: verify DOB against the original, then seed the form with the decrypted pre-fill.
    const verifyResubmit = useCallback(async () => {
        if (!resubmitDob || resubmitBusy) return;
        setResubmitBusy(true);
        setResubmitError(null);
        try {
            const resp = await fetch("/Appointment/ResubmitStart", {
                method: "POST",
                headers: { "Content-Type": "application/json", "RequestVerificationToken": antiForgeryToken() },
                body: JSON.stringify({ Code: resubmitCode, Dob: resubmitDob }),
            });
            const data = await resp.json();
            if (data.ok) {
                setFields(data.fields || {});
                setResubmitStep("tcr");
            } else if (data.status === "locked") {
                setResubmitError("Too many attempts. For your security, please contact the service center.");
            } else if (data.status === "notFound") {
                setResubmitError("We couldn't find that transaction. Check the link from your email, or contact the service center.");
            } else {
                setResubmitError("That date of birth doesn't match this transaction. Please try again.");
            }
        } catch (e) {
            setResubmitError("Something went wrong. Please try again.");
        } finally {
            setResubmitBusy(false);
        }
    }, [resubmitCode, resubmitDob, resubmitBusy]);

    // TCR page: quick local check, then the server's full rule (which re-verifies the DOB and applies the
    // gate). Only an accepted TCR moves on to the (already pre-filled) form. Coming back here via
    // "Change" or a server refusal never re-calls ResubmitStart, so form edits and the slot are kept.
    const continueFromTcr = useCallback(async () => {
        if (resubmitBusy) return;
        const local = checkTcr(tcrInput);
        setTcrError(local.error);
        if (!local.tcr) return;

        setResubmitBusy(true);
        try {
            const resp = await fetch("/Appointment/ResubmitTcr", {
                method: "POST",
                headers: { "Content-Type": "application/json", "RequestVerificationToken": antiForgeryToken() },
                body: JSON.stringify({ Code: resubmitCode, Dob: resubmitDob, Tcr: local.tcr }),
            });
            const data = await resp.json();
            if (data.ok) {
                setTcrInput(data.tcr);
                setResubmitTcr(data.tcr);
                setResubmitStep("form");
            } else if (data.status === "badTcr") {
                setTcrError(data.message || "We can't accept this TCR.");
            } else if (data.status === "locked") {
                setTcrError("Too many attempts. For your security, please contact the service center.");
            } else {
                setTcrError("We couldn't verify the original transaction for this resubmission. Please start again from the link in your email.");
            }
        } catch (e) {
            setTcrError("Something went wrong. Please try again.");
        } finally {
            setResubmitBusy(false);
        }
    }, [tcrInput, resubmitBusy, resubmitCode, resubmitDob]);

    const changeTcr = useCallback(() => {
        setTcrInput(resubmitTcr);
        setTcrError(null);
        setResubmitStep("tcr");
    }, [resubmitTcr]);

    // Field lookup + required-field progress (drives both the gate and the rail note).
    const defById = {};
    CONFIG.fields.forEach((d) => { defById[d.objectId] = d; });
    const requiredDefs = CONFIG.fields.filter((d) => d.required);
    const isDone = (def) => { const v = (fields[def.objectId] || "").trim(); return Boolean(v) && !fieldError(def, fields); };
    const missingDefs = requiredDefs.filter((d) => !isDone(d));
    const total = requiredDefs.length;
    const done = total - missingDefs.length;
    const percent = total ? Math.round((done / total) * 100) : 100;
    const missingLabels = missingDefs.map((d) => shortLabel(d.display));

    // Unlock when every required field is complete AND nothing on the form is invalid.
    const incomplete = CONFIG.fields.filter((def) => {
        const v = (fields[def.objectId] || "").trim();
        return (def.required && !v) || Boolean(fieldError(def, fields));
    });
    const formValid = incomplete.length === 0;

    // Prefer the customer-level window from config (display only); fall back to the location-derived one.
    const win = (CONFIG.schedulingWindowStart && CONFIG.schedulingWindowEnd)
        ? { start: CONFIG.schedulingWindowStart, end: CONFIG.schedulingWindowEnd }
        : computeWindow(avail.locations);

    const setField = (objectId, value) => {
        setFields((prev) => ({ ...prev, [objectId]: value }));
        setErrors((prev) => {
            if (!prev[objectId]) return prev;
            const next = { ...prev };
            delete next[objectId];
            return next;
        });
    };

    // Render the ordered fields for one section (skipping any the field set doesn't define).
    const renderFields = (order) => order
        .map((id) => defById[id])
        .filter(Boolean)
        .map((def) => (
            <Field
                key={def.objectId}
                def={def}
                value={fields[def.objectId]}
                error={errors[def.objectId] || fieldError(def, fields)}
                onChange={setField}
                full={FULL_WIDTH.has(def.objectId)}
            />
        ));

    // Any field defined by the set but not placed in either section — append to Personal Information.
    const placed = new Set([...PERSONAL_ORDER, ...DEMOGRAPHIC_ORDER]);
    const leftover = CONFIG.fields.filter((d) => !placed.has(d.objectId)).map((d) => d.objectId);

    // Book the chosen slot and save the demographics in one request. The server books the slot
    // atomically; if it filled first (409), we clear the selection and refresh the calendar.
    const submit = useCallback(async () => {
        if ((!WALK_IN && !selectedSlot) || submitting) return;
        setSubmitting(true);
        setErrors({});
        setSlotNotice(null);
        try {
            const resp = await fetch("/Intake/Submit", {
                method: "POST",
                headers: { "Content-Type": "application/json", "RequestVerificationToken": antiForgeryToken() },
                body: JSON.stringify({
                    Fields: fields,
                    Groups: {},
                    // A walk-in books nothing; the server ignores any slot on such a host anyway.
                    SlotId: WALK_IN ? undefined : selectedSlot.id,
                    // Echoed into the confirmation email (display only; SlotId is authoritative).
                    Appointment: WALK_IN ? undefined : {
                        locationName: selectedSlot.locationName,
                        address: selectedSlot.address,
                        day: selectedSlot.day,
                        time: selectedSlot.time,
                    },
                    // Resubmission: re-verified server-side, then stamped ACT=RS + the applicant's TCR.
                    ResubmitCode: resubmitCode || undefined,
                    ResubmitDob: resubmitCode ? resubmitDob : undefined,
                    Tcr: resubmitCode ? resubmitTcr : undefined,
                }),
            });
            const data = await resp.json();
            if (data.ok) {
                setConfirmedAppt(selectedSlot);
                setMatchCode(data.matchCode);
            } else if (data.slotFull) {
                setSlotNotice(data.message || "That time just filled. Please pick another available time above.");
                setSelectedSlot(null);
                setReloadKey((k) => k + 1);
            } else if (resubmitCode && (data.errors || []).some((err) => err.objectId === "Tcr")) {
                // The server refused the TCR: back to the TCR page with its message. No field on the
                // form shows a "Tcr" error, so it must not fall through to the per-field map below.
                const tcrErr = data.errors.find((err) => err.objectId === "Tcr");
                setTcrInput(resubmitTcr);
                setTcrError(tcrErr.message);
                setResubmitStep("tcr");
            } else {
                const map = {};
                (data.errors || []).forEach((err) => { if (err.objectId) map[err.objectId] = err.message; });
                if (Object.keys(map).length) {
                    setErrors(map);
                } else {
                    setSlotNotice((data.errors && data.errors[0] && data.errors[0].message) || "We couldn't complete your booking. Please try again.");
                }
            }
        } catch (e) {
            setSlotNotice("Something went wrong while booking. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }, [fields, selectedSlot, submitting, resubmitCode, resubmitDob, resubmitTcr]);

    if (matchCode) {
        return <FollowUp matchCode={matchCode} appt={confirmedAppt} registeredEmail={fields.Email} walkIn={WALK_IN} />;
    }

    // Resubmission DOB gate — shown before the pre-filled form until identity is verified.
    if (resubmitCode && resubmitStep === "dob") {
        return (
            <div className="ai-grid ai-grid--single">
                <div className="ai-card">
                    <div className="ai-card__head">
                        <div>
                            <div className="ai-card__title">Resubmit your application</div>
                            <div className="ai-card__subtitle">Start a new transaction from your previous submission.</div>
                        </div>
                    </div>
                    <div className="ai-card__body">
                        <div className="ai-resubmit-warning" role="alert">
                            <strong>Only resubmit if your transaction was processed and confirmed rejected.</strong>{" "}
                            {WALK_IN ? (
                                <>Resubmitting creates a whole new transaction — if you have not been told your submission
                                was rejected, speak to staff at the enrollment desk instead.</>
                            ) : (
                                <>If you simply want to change your appointment time and have not yet been rejected, go back
                                to your confirmation email and use the <strong>Reschedule</strong> link instead — resubmitting
                                creates a whole new transaction.</>
                            )}
                        </div>
                        <p className="mg-lead">
                            For your security, confirm your date of birth to continue. We'll pre-fill your information so you
                            can correct anything{WALK_IN ? " and submit again." : " and choose a new appointment time."}
                        </p>
                        <div className="ai-fields" style={{ maxWidth: 320 }}>
                            <div className="ai-field">
                                <label className="ai-field__label" htmlFor="rs-dob">Date of Birth<span className="ai-req"> *</span></label>
                                <input id="rs-dob" type="date" value={resubmitDob} onChange={(e) => setResubmitDob(e.target.value)} />
                            </div>
                        </div>
                        {resubmitError ? <p className="intake-schedule-alert" role="alert">{resubmitError}</p> : null}
                        <div className="mg-actions">
                            <button type="button" className="ai-btn-primary" onClick={verifyResubmit} disabled={!resubmitDob || resubmitBusy}>
                                {resubmitBusy ? "Checking…" : "Continue"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Resubmission TCR page — asks only for the TCR, which comes from the applicant's FSO. The
    // pre-filled form and scheduling stay hidden until a valid one is entered.
    if (resubmitCode && resubmitStep === "tcr") {
        return (
            <div className="ai-grid ai-grid--single">
                <div className="ai-card">
                    <div className="ai-card__head">
                        <div>
                            <div className="ai-card__title">Enter your TCR</div>
                            <div className="ai-card__subtitle">Your Transaction Control Reference, from your FSO.</div>
                        </div>
                    </div>
                    <div className="ai-card__body">
                        <p className="mg-lead">
                            Your Facility Security Officer (FSO) gives you a Transaction Control Reference (TCR) when your
                            fingerprints are rejected. Enter it exactly as your FSO gave it to you. If you don't have a TCR,
                            contact your FSO before you resubmit.
                        </p>
                        {/* Not in the 2-column .ai-fields grid: the error runs the card's full width, like the copy above. */}
                        <div className="ai-field ai-field--tcr">
                            <label className="ai-field__label" htmlFor="rs-tcr">TCR<span className="ai-req"> *</span></label>
                            {/* No maxLength: a pasted over-long TCR must show the length message, not be silently cut.
                                Upper-cased as typed, matching how it's stored. */}
                            <input
                                id="rs-tcr"
                                type="text"
                                autoComplete="off"
                                autoCapitalize="characters"
                                spellCheck={false}
                                value={tcrInput}
                                onChange={(e) => {
                                    // Re-rendering a changed value moves the caret to the end; put it back so a
                                    // mid-string edit doesn't jump. (Upper-casing never changes the length.)
                                    const el = e.target;
                                    const start = el.selectionStart, end = el.selectionEnd;
                                    setTcrInput(el.value.toUpperCase());
                                    window.requestAnimationFrame(() => {
                                        try { el.setSelectionRange(start, end); } catch (err) { /* detached */ }
                                    });
                                }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); continueFromTcr(); } }}
                                aria-invalid={tcrError ? "true" : undefined}
                            />
                            {tcrError ? <span className="intake-error" role="alert">{tcrError}</span> : null}
                        </div>
                        <div className="mg-actions">
                            <button type="button" className="ai-btn-primary" onClick={continueFromTcr} disabled={resubmitBusy}>
                                {resubmitBusy ? "Checking…" : "Continue"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ai-grid">
            <div className="ai-main">
                <MobileGate done={done} total={total} percent={percent} missing={missingLabels} ready={formValid} />
                {resubmitCode ? (
                    <div className="ai-resubmit-banner">
                        You're resubmitting a previous application. Your details are pre-filled — correct anything that needs
                        fixing, then {WALK_IN ? "submit again" : "choose a new appointment time"}.
                        <div className="ai-resubmit-banner__tcr">
                            {/* Upper-cased to match what's saved (every stored value is upper-cased). */}
                            TCR: <strong>{resubmitTcr.toUpperCase()}</strong> ·{" "}
                            <button type="button" className="ai-resubmit-banner__change" onClick={changeTcr}>Change</button>
                        </div>
                    </div>
                ) : null}
                {/* Step 1 — Your information */}
                <div className="ai-card">
                    <div className="ai-card__head">
                        <span className="ai-badge">1</span>
                        <div>
                            <div className="ai-card__title">Step 1 — Your information</div>
                            <div className="ai-card__subtitle">Match the name and details on your government-issued ID.</div>
                        </div>
                    </div>
                    <div className="ai-card__body">
                        <div className="ai-section-eyebrow">Personal Information</div>
                        <div className="ai-fields">
                            {renderFields(PERSONAL_ORDER)}
                            {renderFields(leftover)}
                        </div>

                        <div className="ai-divider" />

                        <div className="ai-section-eyebrow">Demographics</div>
                        <div className="ai-fields">
                            {renderFields(DEMOGRAPHIC_ORDER)}
                        </div>
                    </div>
                </div>

                {/* Step 2 — schedule, or (walk-in) just review and submit */}
                <div className="ai-card">
                    <div className="ai-card__head">
                        <span className="ai-badge">2</span>
                        <div>
                            <div className="ai-card__title">
                                {WALK_IN ? "Step 2 — Review and submit" : "Step 2 — Schedule your appointment"}
                            </div>
                            <div className="ai-card__subtitle">
                                {WALK_IN
                                    ? "Check your details above, then submit."
                                    : "Pick a location, then a date and time."}
                            </div>
                        </div>
                        <span className={"ai-chip " + (formValid ? "ai-chip--ready" : "ai-chip--locked")}>
                            {formValid ? "Ready" : "Locked"}
                        </span>
                    </div>
                    <div className={"ai-card__body ai-step2" + (formValid ? "" : " is-locked")} aria-disabled={!formValid}>
                        {WALK_IN ? (
                            <p className="ai-note-empty">
                                You're already on site — no appointment needed. Submit your information and show the
                                confirmation code to the technician.
                            </p>
                        ) : avail.loading ? (
                            <p className="ai-note-empty">Loading available times…</p>
                        ) : avail.error ? (
                            <p className="intake-error" role="alert">We couldn't load available times. Please try again in a moment.</p>
                        ) : (
                            <Scheduler
                                locations={avail.locations}
                                selectedId={selectedSlot ? selectedSlot.id : null}
                                onSelect={(s) => { setSelectedSlot(s); setSlotNotice(null); }}
                                disabled={!formValid}
                            />
                        )}

                        {slotNotice ? <p className="intake-schedule-alert" role="alert">{slotNotice}</p> : null}

                        <div className="ai-confirm-row">
                            <button type="button" className="ai-btn-primary" onClick={submit}
                                disabled={!formValid || (!WALK_IN && !selectedSlot) || submitting}>
                                {submitting
                                    ? (WALK_IN ? "Submitting…" : "Booking…")
                                    : (WALK_IN ? "Submit →" : "Confirm appointment →")}
                            </button>
                            <span className="ai-confirm-help">
                                {WALK_IN
                                    ? "You'll receive a confirmation email with your code."
                                    : selectedSlot
                                        ? <>Selected: <strong>{selectedSlot.locationName}</strong> — {selectedSlot.day} at {selectedSlot.time}</>
                                        : "You'll receive a confirmation email with your appointment details."}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky right rail */}
            <aside className="ai-rail">
                <GateNote done={done} total={total} percent={percent} missing={missingLabels} />

                {WALK_IN ? (
                    <div className="ai-rail-card">
                        <div className="ai-rail-card__title">Walk-in enrollment</div>
                        <p className="ai-rail-card__body">
                            No appointment is needed. Submit your information here, then wait to be called.
                        </p>
                        <div className="ai-rail-card__divider" />
                        <p className="ai-rail-card__note">
                            Have a government-issued photo ID ready for the technician.
                        </p>
                    </div>
                ) : (
                <div className="ai-rail-card">
                    <div className="ai-rail-card__title">Scheduling window</div>
                    {win ? (
                        <p className="ai-rail-card__body">
                            Appointments are available <strong>{formatLongDate(win.start)}</strong> through <strong>{formatLongDate(win.end)}</strong>.
                        </p>
                    ) : (
                        <p className="ai-rail-card__body">Appointment availability will appear here.</p>
                    )}
                    <div className="ai-rail-card__divider" />
                    <p className="ai-rail-card__note">
                        Bring a government-issued photo ID to your appointment. Arrive 15 minutes early.
                    </p>
                </div>
                )}

                <div className="ai-rail-card">
                    <div className="ai-rail-card__title">Need help?</div>
                    <p className="ai-rail-card__body">
                        Questions about your enrollment? Our help page has the details and how to reach us.
                    </p>
                    {/* Opens in a new tab so an in-progress form is not lost. */}
                    <a className="ai-rail-card__btn" href="/Intake/Help" target="_blank" rel="noopener">
                        Help
                    </a>
                </div>
            </aside>
        </div>
    );
}

const root = document.getElementById("intake-root");
if (root) {
    ReactDOM.render(<IntakeForm />, root);
}

export default IntakeForm;
